/** The rectangle a set of positions covers. */
export interface PositionBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** How much of the map to leave free around the outermost vehicles. */
const EDGE_PADDING = 0.85;
/** Nobody wants to be dropped onto a single vehicle at maximum zoom. */
const MAX_FIT_ZOOM = 16;
const MIN_FIT_ZOOM = 2;
/** Web Mercator at zoom 0 is 512 pixels wide in deck.gl's reckoning. */
const WORLD_SIZE_PX = 512;

export function boundsOfPositions(
  positions: Iterable<readonly [number, number]>,
): PositionBounds | null {
  let west = Number.POSITIVE_INFINITY;
  let south = Number.POSITIVE_INFINITY;
  let east = Number.NEGATIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;
  let found = false;

  for (const [longitude, latitude] of positions) {
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      continue;
    }
    found = true;
    west = Math.min(west, longitude);
    east = Math.max(east, longitude);
    south = Math.min(south, latitude);
    north = Math.max(north, latitude);
  }

  return found ? { west, south, east, north } : null;
}

/**
 * Where to look so that everything fits on screen.
 *
 * Worked out here rather than with deck.gl's own `fitBounds` so it can be tested without a
 * viewport, and so a fleet standing in one yard gets a sensible zoom instead of the maximum.
 *
 * @param bounds - What has to be visible.
 * @param width - The map's width in pixels.
 * @param height - The map's height in pixels.
 */
export function viewportForBounds(
  bounds: PositionBounds,
  width: number,
  height: number,
): { longitude: number; latitude: number; zoom: number } {
  const longitude = (bounds.west + bounds.east) / 2;
  const latitude = (bounds.south + bounds.north) / 2;

  if (width <= 0 || height <= 0) {
    return { longitude, latitude, zoom: MIN_FIT_ZOOM };
  }

  const longitudeSpan = Math.abs(bounds.east - bounds.west);
  const latitudeSpan = Math.abs(mercatorY(bounds.north) - mercatorY(bounds.south));

  // Everything in one spot: there is no span to fit, so stay close but not on top of it.
  if (longitudeSpan < 1e-9 && latitudeSpan < 1e-9) {
    return { longitude, latitude, zoom: MAX_FIT_ZOOM };
  }

  const zoomForLongitude = zoomForSpan(longitudeSpan / 360, width);
  const zoomForLatitude = zoomForSpan(latitudeSpan, height);
  const zoom = Math.min(
    MAX_FIT_ZOOM,
    Math.max(MIN_FIT_ZOOM, Math.min(zoomForLongitude, zoomForLatitude)),
  );

  return { longitude, latitude, zoom };
}

/** Latitude as its share of the Mercator world, 0 at the south edge and 1 at the north. */
function mercatorY(latitude: number): number {
  const clamped = Math.min(85.05112878, Math.max(-85.05112878, latitude));
  const radians = (clamped * Math.PI) / 180;
  return 0.5 - Math.log(Math.tan(Math.PI / 4 + radians / 2)) / (2 * Math.PI);
}

function zoomForSpan(worldFraction: number, sizePx: number): number {
  if (worldFraction <= 0) {
    return MAX_FIT_ZOOM;
  }
  return Math.log2((sizePx * EDGE_PADDING) / (worldFraction * WORLD_SIZE_PX));
}
